import { useState, useCallback } from "react";
import { Copy, Check, ExternalLink, Phone } from "lucide-react";
import { toast } from "sonner";

function buildWhatsAppMessage({ clientName, plate, vehicleInfo }) {
  const name = (clientName || "").trim() || "cliente de Ascauto";
  const vehicle = (vehicleInfo || "").trim();
  const plateClean = (plate || "").trim();

  if (vehicle && plateClean) {
    return `Hola, soy ${name}. Quería contactar contigo sobre mi ${vehicle} con matrícula ${plateClean}.`;
  }
  if (vehicle) {
    return `Hola, soy ${name}. Quería contactar contigo sobre mi ${vehicle}.`;
  }
  if (plateClean) {
    return `Hola, soy ${name}. Quería contactar contigo sobre mi vehículo con matrícula ${plateClean}.`;
  }
  return `Hola, soy ${name}. Quería contactar con mi asesor de servicio.`;
}

function buildWhatsAppUrl(phoneRaw, message) {
  const num = phoneRaw.replace(/[^0-9]/g, "");
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${num}?text=${encoded}`;
}

function formatPhoneDisplay(phone) {
  const num = phone.replace(/[^0-9]/g, "");
  if (num.length === 11 && num.startsWith("34")) {
    const local = num.slice(2);
    return `+34 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  if (num.length === 9) {
    return `${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6)}`;
  }
  return phone;
}

export function WhatsAppContactBlock({
  phone,
  clientName,
  plate,
  vehicleInfo,
  label = "Hablar por WhatsApp",
  testIdPrefix = "wa",
}) {
  const [copiedNum, setCopiedNum] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);

  const message = buildWhatsAppMessage({ clientName, plate, vehicleInfo });
  const waUrl = buildWhatsAppUrl(phone, message);
  const phoneDisplay = formatPhoneDisplay(phone);

  const copyToClipboard = useCallback(async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "num") { setCopiedNum(true); setTimeout(() => setCopiedNum(false), 2000); }
      else { setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 2000); }
      toast.success(type === "num" ? "Numero copiado" : "Mensaje copiado");
    } catch {
      toast.error("No se pudo copiar al portapapeles");
    }
  }, []);

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-contact-block`}>
      {/* Primary CTA: Native <a> with target=_top to break out of iframe/preview */}
      <a
        href={waUrl}
        target="_top"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-sm h-14 text-sm uppercase tracking-wider font-bold shadow-md hover:shadow-lg transition-all"
        data-testid={`${testIdPrefix}-primary-btn`}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 flex-shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        {label}
      </a>

      {/* Fallback section: always visible so user is never stuck */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3" data-testid={`${testIdPrefix}-fallback`}>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          Si no se abre automaticamente, usa estas opciones:
        </p>

        {/* Phone number visible + copy */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-mono font-bold text-slate-900" data-testid={`${testIdPrefix}-phone-display`}>
              {phoneDisplay}
            </span>
          </div>
          <button
            type="button"
            onClick={() => copyToClipboard(phone.replace(/[^0-9+]/g, ""), "num")}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 transition-colors"
            data-testid={`${testIdPrefix}-copy-phone-btn`}
          >
            {copiedNum ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            {copiedNum ? "Copiado" : "Copiar numero"}
          </button>
        </div>

        {/* Copy message */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500 truncate max-w-[60%]" title={message}>
            {message.slice(0, 60)}...
          </p>
          <button
            type="button"
            onClick={() => copyToClipboard(message, "msg")}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded border border-slate-200 hover:border-slate-300 transition-colors"
            data-testid={`${testIdPrefix}-copy-msg-btn`}
          >
            {copiedMsg ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            {copiedMsg ? "Copiado" : "Copiar mensaje"}
          </button>
        </div>

        {/* Manual link - opens in new tab as secondary fallback */}
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full text-[#25D366] border border-[#25D366] hover:bg-[#25D366]/5 rounded-sm h-10 text-xs uppercase tracking-wider font-bold transition-colors"
          data-testid={`${testIdPrefix}-manual-link`}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Abrir WhatsApp en nueva pestana
        </a>
      </div>
    </div>
  );
}
